import { useQuery, queryOptions } from '@tanstack/react-query'
import pyqService from '../services/pyqService'
import syllabusService from '@/features/syllabus/services/syllabusService'
import { SYLLABUS_CODE } from '../types'

// Query keys
export const pyqKeys = {
  all: ['pyq'] as const,
  questionIds: (nodeCode: string) => [...pyqKeys.all, 'questionIds', nodeCode] as const,
  question: (id: string) => [...pyqKeys.all, 'question', id] as const,
  subjects: (syllabusCode: string) => [...pyqKeys.all, 'subjects', syllabusCode] as const,
  units: (syllabusCode: string, subjectCode: string) =>
    [...pyqKeys.all, 'units', syllabusCode, subjectCode] as const,
  exams: () => [...pyqKeys.all, 'exams'] as const,
  hierarchy: (syllabusCode: string) => [...pyqKeys.all, 'hierarchy', syllabusCode] as const,
  nodeQuestions: (nodeCode: string) => [...pyqKeys.all, 'nodeQuestions', nodeCode] as const,
}

/**
 * Hook to fetch available PYQ exams, grouped by year (newest first)
 */
export function usePyqExams() {
  return useQuery({
    queryKey: pyqKeys.exams(),
    queryFn: async () => {
      const response = await pyqService.getPyqExams()
      return response.data.years
    },
    staleTime: 1000 * 60 * 30, // 30 minutes
  })
}

/**
 * Hook to fetch subjects from syllabus
 */
export function useSubjects(syllabusCode: string) {
  return useQuery({
    queryKey: pyqKeys.subjects(syllabusCode),
    queryFn: async () => {
      const response = await syllabusService.getNodesBySyllabus(syllabusCode, {
        nodeType: 'SUBJECT',
        isActive: true,
      })
      return response.data.nodes
    },
    staleTime: 1000 * 60 * 30, // 30 minutes - subjects don't change often
    enabled: !!syllabusCode,
  })
}

/**
 * Hook to fetch units for a subject
 */
export function useUnits(syllabusCode: string, subjectCode: string) {
  return useQuery({
    queryKey: pyqKeys.units(syllabusCode, subjectCode),
    queryFn: async () => {
      const response = await syllabusService.getNodesBySyllabus(syllabusCode, {
        nodeType: 'UNIT',
        parentNodeCode: subjectCode,
        isActive: true,
      })
      return response.data.nodes
    },
    staleTime: 1000 * 60 * 30, // 30 minutes
    enabled: !!syllabusCode && !!subjectCode,
  })
}

/**
 * Hook to fetch question IDs for a unit (Step 1)
 * Gets all question mappings for a node code
 */
export function useQuestionIds(nodeCode: string, enabled: boolean = true) {
  return useQuery({
    queryKey: pyqKeys.questionIds(nodeCode),
    queryFn: async () => {
      const result = await pyqService.getQuestionsForUnit(nodeCode, true)
      return result.questionIds
    },
    staleTime: 1000 * 60 * 10, // 10 minutes
    enabled: enabled && !!nodeCode,
  })
}

/**
 * Query options for a single question by ID (full details incl. solution).
 * Shared so pages can prefetch the next question.
 */
export function questionQueryOptions(questionId: string) {
  return queryOptions({
    queryKey: pyqKeys.question(questionId),
    queryFn: async () => {
      const response = await pyqService.getQuestionById(questionId)
      return response.data
    },
    staleTime: 1000 * 60 * 10, // 10 minutes
  })
}

/**
 * Hook to get a single question by ID (for full details)
 * Used when user navigates to a specific question
 */
export function useQuestion(questionId: string, enabled: boolean = true) {
  return useQuery({
    ...questionQueryOptions(questionId),
    enabled: enabled && !!questionId,
  })
}

/**
 * Hook to fetch one subject's subtree from the NEET_UG hierarchy.
 * The hierarchy is fetched once and shared by all subjects.
 */
export function useSubjectTree(subjectCode: string) {
  return useQuery({
    queryKey: pyqKeys.hierarchy(SYLLABUS_CODE),
    queryFn: async () => {
      const response = await syllabusService.getSyllabusHierarchy(SYLLABUS_CODE)
      return response.data
    },
    select: (hierarchy) => hierarchy.rootNodes.find((node) => node.nodeCode === subjectCode),
    staleTime: 1000 * 60 * 30, // 30 minutes - syllabus doesn't change often
  })
}

/**
 * Hook to fetch summaries of all PYQs mapped to a syllabus node (incl. descendants)
 */
export function useNodeQuestions(nodeCode: string, enabled: boolean = true) {
  return useQuery({
    queryKey: pyqKeys.nodeQuestions(nodeCode),
    queryFn: () => pyqService.getQuestionSummariesByNode(nodeCode),
    staleTime: 1000 * 60 * 10, // 10 minutes
    enabled: enabled && !!nodeCode,
  })
}
